/**
 * Small, pure geometry helpers shared by the handwriting recognizer, the
 * segmenter and the stroke synthesiser. DOM-free so it runs in node tests.
 */

export interface Pt {
  x: number;
  y: number;
}

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type Polyline = readonly Pt[];

export const EMPTY_BOX: Box = { x: 0, y: 0, w: 0, h: 0 };

export const dist = (a: Pt, b: Pt): number => Math.hypot(a.x - b.x, a.y - b.y);

/** Linear ramp: 0 at `from`, 1 at `to` (works for descending ramps too), clamped. */
export function ramp(x: number, from: number, to: number): number {
  if (from === to) return x >= to ? 1 : 0;
  const t = (x - from) / (to - from);
  return t <= 0 ? 0 : t >= 1 ? 1 : t;
}

/** Axis-aligned bounding box of any number of polylines (EMPTY_BOX when there are no points). */
export function boundsOf(lines: readonly Polyline[]): Box {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const line of lines) {
    for (const p of line) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
  }
  if (minX === Infinity) return EMPTY_BOX;
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

export function unionBox(a: Box, b: Box): Box {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return { x, y, w: Math.max(a.x + a.w, b.x + b.w) - x, h: Math.max(a.y + a.h, b.y + b.h) - y };
}

export function pathLength(line: Polyline): number {
  let len = 0;
  for (let i = 1; i < line.length; i++) len += dist(line[i - 1]!, line[i]!);
  return len;
}

/**
 * Resample one polyline to exactly `m` points spaced evenly along its length.
 * m = 1 returns the point halfway along the path (a good anchor for dots).
 */
export function resampleLine(line: Polyline, m: number): Pt[] {
  if (m <= 0 || line.length === 0) return [];
  const first = line[0]!;
  const total = pathLength(line);
  if (total === 0) return Array.from({ length: m }, () => ({ x: first.x, y: first.y }));
  const targets = m === 1 ? [total / 2] : Array.from({ length: m }, (_, i) => (total * i) / (m - 1));
  const out: Pt[] = [];
  let walked = 0;
  let seg = 1;
  for (const target of targets) {
    // Advance to the segment that contains `target`.
    while (seg < line.length - 1 && walked + dist(line[seg - 1]!, line[seg]!) < target) {
      walked += dist(line[seg - 1]!, line[seg]!);
      seg++;
    }
    const a = line[seg - 1]!;
    const b = line[seg] ?? a;
    const d = dist(a, b);
    const t = d === 0 ? 0 : Math.min(1, Math.max(0, (target - walked) / d));
    out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
  }
  return out;
}

/**
 * Resample several strokes to `n` points in total, returned per stroke.
 * Points are shared out in proportion to stroke length (largest-remainder
 * method) with at least one point per stroke, and never interpolated across
 * the pen-up gap between strokes — the $P convention.
 */
export function resampleStrokes(strokes: readonly Polyline[], n: number): Pt[][] {
  const lines = strokes.filter((s) => s.length > 0);
  if (!lines.length || n <= 0) return [];
  const lengths = lines.map(pathLength);
  const total = lengths.reduce((s, x) => s + x, 0);
  const shares = total > 0 ? lengths.map((l) => (n * l) / total) : lines.map(() => n / lines.length);
  const minEach = lines.length <= n ? 1 : 0;
  const counts = shares.map((s) => Math.max(minEach, Math.floor(s)));
  let sum = counts.reduce((s, x) => s + x, 0);
  while (sum > n) {
    // Take a point from the stroke with the most points to spare.
    let k = 0;
    for (let i = 1; i < counts.length; i++) if (counts[i]! > counts[k]!) k = i;
    counts[k]!--;
    sum--;
  }
  while (sum < n) {
    // Give a point to the stroke that was rounded down the most.
    let k = 0;
    let best = -Infinity;
    for (let i = 0; i < counts.length; i++) {
      const deficit = shares[i]! - counts[i]!;
      if (deficit > best) {
        best = deficit;
        k = i;
      }
    }
    counts[k]!++;
    sum++;
  }
  return lines.map((line, i) => resampleLine(line, counts[i]!)).filter((l) => l.length > 0);
}

export function centroid(points: readonly Pt[]): Pt {
  let x = 0;
  let y = 0;
  for (const p of points) {
    x += p.x;
    y += p.y;
  }
  const n = points.length || 1;
  return { x: x / n, y: y / n };
}

/**
 * $P normalisation: uniform scale so the larger bounding-box side becomes 1
 * (aspect ratio is kept — a "1" stays thin), then translate the centroid to the origin.
 */
export function normalizeCloud(points: readonly Pt[]): Pt[] {
  const box = boundsOf([points]);
  const size = Math.max(box.w, box.h) || 1;
  const scaled = points.map((p) => ({ x: (p.x - box.x) / size, y: (p.y - box.y) / size }));
  const c = centroid(scaled);
  return scaled.map((p) => ({ x: p.x - c.x, y: p.y - c.y }));
}

/** Rotate points by `rad` around `about`. */
export function rotate(points: readonly Pt[], rad: number, about: Pt): Pt[] {
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return points.map((p) => {
    const dx = p.x - about.x;
    const dy = p.y - about.y;
    return { x: about.x + dx * cos - dy * sin, y: about.y + dx * sin + dy * cos };
  });
}

/** Horizontal overlap of two boxes (0 when they don't overlap). */
export function xOverlap(a: Box, b: Box): number {
  return Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x));
}
